import React, { useState } from "react"

import { PropTypes } from "prop-types"
import { format } from "date-fns"

import { toFirstLetterUppercase, findAll } from "../../../helpers/helpers"
import { SampleLetterField } from "../sample-letter-field/sample-letter-field"
import { SampleLetterPrint } from "../sample-letter-print/sample-letter-print"

import "./sample-letter-form.scss"

export const SampleLetterForm = ({
  title,
  instructions,
  description,
  sampleLetterContent,
  submitForm,
  isRelevantToCurrentRegion,
}) => {
  const [isJoint, setIsJoint] = useState(false)

  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)

  const [letterContent, setLetterContent] = useState(sampleLetterContent)

  const fullTitle = `${title} (${isJoint ? "joint names" : "sole name"})`

  const handleSubmitForm = e => {
    e.preventDefault()

    const isTemplate = e.currentTarget.getAttribute("data-is-template")

    setIsDownloadingTemplate(isTemplate)

    amendInputsForPrint(isTemplate)

    if (isTemplate || isFormValid(e)) {
      // Set timeout so that form has time to re-render with/without branding.
      setTimeout(() => {
        submitForm(e)

        if (isTemplate) {
          setIsDownloadingTemplate(false)
          document.getElementById("sample-letter-form").reset()
        }
      }, 750)
    }
  }

  const amendInputsForPrint = isTemplate => {
    const inputs = document.getElementsByClassName(
      "sample-letter-field-control"
    )
    Array.from(inputs).forEach(input => {
      if (isTemplate) {
        input.classList.remove("hide-placeholder-print")
        input.value = null
      } else {
        input.classList.add("hide-placeholder-print")
      }
    })
  }

  const isFormValid = ({ target }) => {
    let isValid = true
    const formData = new FormData(target)
    for (const [key, value] of formData.entries()) {
      const element = document.getElementById(key)
      const isRequired = element.hasAttribute("required")

      // Remove invalid class incase element was previously invalid.
      element.classList.remove("is-invalid")

      if (isRequired && value === "") {
        element.classList.add("is-invalid")
        isValid = false
      }
    }
    return isValid
  }

  const parseLetterToParagraphs = letter =>
    letter
      .replace(/{/g, "<strong>")
      .replace(/}/g, "</strong>")
      .replace(/<\/p>/g, "")
      .split("<p>")
      .filter(str => str && str !== "" && str !== " ")

  const switchMode = () => {
    setIsJoint(!isJoint)

    // Patterns that need to be replaced to convert
    // the mode into a "joint" letter or vice versa.
    const replacements = {
      "\\. I am": "\\. We are",
      " I am": " we are",
      "I am": "We are",
      "\\. I ": "\\. We ",
      " I ": " we ",
      ", I ": ", we ",
      "I ": "We ",
      " my ": " our ",
      "My ": "Our ",
      " me ": " us ",
      " me\\.": " us\\.",
    }

    let amendedLetterContent = letterContent

    Object.keys(replacements).forEach(key => {
      const replacement = isJoint ? key : replacements[key]
      const toBeReplaced = isJoint ? replacements[key] : key

      amendedLetterContent = amendedLetterContent.replace(
        new RegExp(toBeReplaced, "g"),
        replacement.replace(/\\|\//g, "")
      )
    })

    setLetterContent(amendedLetterContent)
  }

  return (
    <form
      id="sample-letter-form"
      onSubmit={handleSubmitForm}
      className={`
        sample-letter-form 
        ${isDownloadingTemplate ? "no-box-shadow" : ""}
      `}
      autoComplete="on"
    >
      <div className="row">
        <div className="col-12 mb-4 mb-sm-0 mt-2 mt-sm-0 col-sm-3 col-lg-2 d-print-none pr-sm-0">
          <button
            type="submit"
            className="btn btn-secondary btn-icon download-icon w-100 text-center"
          >
            Print letter
          </button>
        </div>

        <div className="col-12 col-lg-10 mt-3 mt-lg-0 pl-lg-5">
          <div className="d-print-none">
            <h1>Sample Letter</h1>
            <h2 className="h3">{fullTitle}</h2>

            <div className="row">
              <div
                className="col-12 col-lg-7"
                dangerouslySetInnerHTML={{ __html: instructions }}
              />

              <div className="col sample-letter-form-joint-panel">
                <h3
                  className={`
                    h4 
                    d-flex 
                    align-items-center 
                    justify-content-between 
                    sample-letter-form-joint-panel-title 
                    ${isJoint ? "sole" : "joint"}
                  `}
                >
                  Letter in {isJoint ? "sole" : "joint"} names
                </h3>
                <p>
                  You can choose to send a letter&nbsp;
                  <strong>in your own name or in joint names</strong>. You may
                  have a debt in joint names, or want to write to your creditors
                  together because you have worked out a joint budget.
                </p>

                <div
                  role="button"
                  tabIndex="0"
                  className="btn btn-primary btn-icon chevron-icon text-white w-100"
                  onKeyPress={({ key }) => key === "Enter" && switchMode()}
                  onClick={switchMode}
                >
                  Change to
                  <strong>&nbsp;{isJoint ? "sole" : "joint"}&nbsp;</strong>
                  version
                </div>
              </div>
            </div>
          </div>

          {isDownloadingTemplate && (
            <SampleLetterPrint
              title={fullTitle}
              instructions={description}
              isRelevantToCurrentRegion={isRelevantToCurrentRegion}
            />
          )}

          <div className="card card-hollow card-dashed mt-4">
            <div className="card-body sample-letter-form-body">
              <div className="row">
                <div className="form-group col-sm-5 sample-letter-form-body-their-address">
                  <SampleLetterField
                    isRequired
                    id="nameOfOrg"
                    label="Name of the organisation you are writing to"
                    hint="Include the name of the person or creditor you are writing to."
                    invalidMessage="You need to include the organisation name."
                  />
                  <SampleLetterField
                    isRequired
                    id="theirAddress1"
                    label="Their address line 1"
                    hint="Make sure you include the full address for the person or organisation. Don't forget the post code."
                    invalidMessage="You need to include their address."
                  />
                  <SampleLetterField
                    id="theirAddress2"
                    label="Their address line 2"
                  />
                  <SampleLetterField
                    id="theirAddress3"
                    label="Their address line 3"
                  />
                  <SampleLetterField
                    isRequired
                    id="theirAddress4"
                    label="Their postcode"
                    hint="Don't forget their postcode."
                    invalidMessage="You need to include their postcode."
                  />
                </div>

                <div className="form-group col-sm-5 offset-md-2">
                  <SampleLetterField
                    isRequired
                    align="right"
                    id="yourAddress1"
                    autoComplete="address-line1"
                    label="Your address line 1"
                    hint="Make sure you include your full address."
                    invalidMessage="You need to include your address."
                  />
                  <SampleLetterField
                    id="yourAddress2"
                    label="Your address line 2"
                    autoComplete="address-line2"
                    align="right"
                  />
                  <SampleLetterField
                    align="right"
                    id="yourAddress3"
                    autoComplete="address-line3"
                    label="Your address line 3"
                  />
                  <SampleLetterField
                    isRequired
                    align="right"
                    id="yourAddress4"
                    autoComplete="postal-code"
                    label="Your postcode"
                    hint="Don't forget your postcode."
                    invalidMessage="You need to include your postcode."
                  />

                  <p
                    className={`${
                      isDownloadingTemplate ? "d-print-none" : ""
                    } text-right mt-3 sample-letter-form-date`}
                  >
                    {format(new Date(), "do MMMM Y")}
                  </p>
                  <SampleLetterField
                    id="date"
                    align="right"
                    autoComplete="date"
                    label="Date"
                    className="d-none d-print-inline"
                  />
                </div>
              </div>

              <div>
                {parseLetterToParagraphs(letterContent).map(
                  (paragraph, index) => {
                    const hasInputs = [...findAll(/\[(.*?)\]/g, paragraph)]
                      .length

                    if (hasInputs) {
                      // Split paragraph by arbitrary [ ] input tags.
                      paragraph = paragraph.split(/\[|\]/g)
                    }

                    const isBlockInput =
                      hasInputs && paragraph[0] === "" && paragraph[2] === ""

                    return (
                      <div className="row" key={index}>
                        <div className="col-12">
                          <div className="p mb-3">
                            {Array.isArray(paragraph) ? (
                              paragraph.map((element, i) => {
                                // We can determine that all odd elements in the array are inputs as the
                                // 'split' method splits the target content into the odd slots of the array.
                                const isInput = i % 2

                                if (isInput) {
                                  // Split the input into the label and hint
                                  // based on arbitrary || characters.
                                  const labelAndProperties = element.split("||")

                                  const label = toFirstLetterUppercase(
                                    labelAndProperties[0]
                                  )

                                  const isTextarea =
                                    labelAndProperties[2] &&
                                    labelAndProperties[2]
                                      .trim()
                                      .toLowerCase() === "textarea"

                                  return (
                                    <SampleLetterField
                                      key={`${index}-${i}`}
                                      isRequired
                                      align="center"
                                      label={label}
                                      id={`dynamicSampleLetterFieldNo${index}`}
                                      isTextarea={isTextarea}
                                      isInline={!isBlockInput}
                                      invalidMessage="This field is required."
                                      hint={labelAndProperties[1] || ""}
                                    />
                                  )
                                } else if (element)
                                  return (
                                    <span
                                      key={`${index}-${i}`}
                                      className="mr-1"
                                      dangerouslySetInnerHTML={{
                                        __html: element.trimRight(),
                                      }}
                                    />
                                  )
                                else return <React.Fragment key={index} />
                              })
                            ) : (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: paragraph.trim(),
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }
                )}
              </div>

              <div className="row">
                <div className="col-12">
                  <SampleLetterField
                    isInline
                    isRequired
                    id="yourName"
                    autoComplete="name"
                    invalidMessage="You need to include your name(s)."
                    label={`Include ${isJoint ? "both " : ""}your full name${
                      isJoint ? "s" : ""
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex">
            <button
              type="submit"
              aria-label="Print letter without populated fields and with instructions"
              className="btn btn-secondary btn-icon download-icon mt-3 d-print-none"
            >
              Print letter
            </button>
          </div>

          <div className="d-print-none">
            <hr className="mt-4 mb-4 d-block" />

            <p className="d-flex align-items-center justify-content-end">
              <button
                type="submit"
                aria-label="Print letter with populated fields"
                data-is-template={true}
                onClick={handleSubmitForm}
                formNoValidate={true}
                className="btn btn-sm btn-outline-secondary ml-2 download-dark-icon d-print-none"
              >
                Print template
              </button>
            </p>
          </div>
        </div>
      </div>
    </form>
  )
}

SampleLetterForm.propTypes = {
  title: PropTypes.string,
  instructions: PropTypes.string,
  description: PropTypes.string,
  sampleLetterContent: PropTypes.string,
  submitForm: PropTypes.func,
  isRelevantToCurrentRegion: PropTypes.bool,
}
